import type { CountdownBlock, ViewportSize } from "@aswin.dev/types";
import type { CountdownSegment } from "../../../composables/useCountdownTimer";

export interface CountdownStyleProps {
  block: CountdownBlock;
  viewport: ViewportSize;
  segments: CountdownSegment[];
  separator: string;
}

export interface LayoutTokens {
  /** Whether segments stack vertically. */
  stack: boolean;
  /** CSS gap between segments in pixels. */
  gap: number;
  /** Container padding (px). */
  padding: number;
  /** Whether the container should fill the viewport (fullscreen layout). */
  fullscreen: boolean;
  /** Multiplier applied to digit font size for compact layouts. */
  digitScale: number;
}

export function resolveLayoutTokens(
  block: CountdownBlock,
  viewport: ViewportSize,
): LayoutTokens {
  const layout = block.layout ?? "horizontal";
  const isMobile = viewport === "mobile";
  const baseGap = block.cardGap ?? 12;

  switch (layout) {
    case "vertical":
      return {
        stack: true,
        gap: baseGap + 4,
        padding: isMobile ? 16 : 24,
        fullscreen: false,
        digitScale: 1,
      };
    case "compact":
      return {
        stack: false,
        gap: Math.max(4, baseGap - 6),
        padding: isMobile ? 8 : 12,
        fullscreen: false,
        digitScale: 0.7,
      };
    case "fullscreen":
      return {
        stack: false,
        gap: isMobile ? baseGap : baseGap + 16,
        padding: isMobile ? 32 : 64,
        fullscreen: true,
        digitScale: isMobile ? 1.1 : 1.6,
      };
    default:
      return {
        stack: isMobile,
        gap: baseGap,
        padding: isMobile ? 16 : 24,
        fullscreen: false,
        digitScale: 1,
      };
  }
}

export function speedDuration(
  speed: CountdownBlock["animationSpeed"],
  base: number,
): number {
  switch (speed) {
    case "slow":
      return Math.round(base * 1.6);
    case "fast":
      return Math.round(base * 0.65);
    default:
      return base;
  }
}

/** Returns a hex color with adjusted alpha (0-1). Falls back gracefully if not hex. */
export function withAlpha(color: string, alpha: number): string {
  if (!color) return `rgba(0,0,0,${alpha})`;
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex.slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return color;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}
